// ==========================================
// 1. AUDIO SYNTHESIZER (Web Audio API)
// ==========================================
export class SoundEngine {
  constructor() {
    this.ctx = null;
    this.enabled = true;
    /** Niveau per kanaal (0 tot 1): motor en weer, sirenes en toeters, effecten. Zie mix.ts. */
    this.levels = { motor: 1, alarm: 1, effects: 1 };
    this.engineOsc = null;
    this.engineGain = null;
    this.skidGain = null;
    this.skidNoise = null;
    this.rainGain = null;
    this.rainNoise = null;
    this.rainFilter = null;
    this.windGain = null;
    this.windOsc = null;
    this.hornOsc1 = null;
    this.hornOsc2 = null;
    this.hornGain = null;
    this.isHonking = false;
    this.isInitialized = false;
  }

  init() {
    if (this.isInitialized) return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      this.ctx = new AudioCtx();

      // Mix: achtergrond (motor, banden, weer) en effecten apart, samen door een begrenzer.
      // Zo kan de motor even zachter bij meldingen, en klippen stapelende geluiden niet.
      this.master = this.ctx.createGain();
      this.master.gain.value = this.enabled ? 1 : 0;
      const limiter = this.ctx.createDynamicsCompressor();
      limiter.threshold.value = -10;
      limiter.ratio.value = 12;
      limiter.attack.value = 0.003;
      limiter.release.value = 0.15;
      this.master.connect(limiter);
      limiter.connect(this.ctx.destination);
      // Elk kanaal heeft een eigen niveau (instellingen); ambientBus zelf wordt door duck() bewogen.
      this.ambientLevel = this.ctx.createGain();
      this.ambientLevel.gain.value = this.levels.motor;
      this.ambientLevel.connect(this.master);
      this.ambientBus = this.ctx.createGain();
      this.ambientBus.gain.value = 0.85;
      this.ambientBus.connect(this.ambientLevel);
      this.sfxBus = this.ctx.createGain();
      this.sfxBus.connect(this.master);
      this.alertBus = this.ctx.createGain();
      this.alertBus.gain.value = this.levels.alarm;
      this.alertBus.connect(this.sfxBus);
      this.effectBus = this.ctx.createGain();
      this.effectBus.gain.value = this.levels.effects;
      this.effectBus.connect(this.sfxBus);

      // Engine oscillator (2-stroke scooter hum)
      this.engineOsc = this.ctx.createOscillator();
      this.engineOsc.type = 'triangle';
      this.engineOsc.frequency.setValueAtTime(55, this.ctx.currentTime);

      this.engineGain = this.ctx.createGain();
      this.engineGain.gain.setValueAtTime(0.0001, this.ctx.currentTime);

      this.engineOsc.connect(this.engineGain);
      this.engineGain.connect(this.ambientBus);
      this.engineOsc.start();

      // Skid noise buffer
      const bufferSize = this.ctx.sampleRate * 1;
      const noiseBuffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
      const output = noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        output[i] = (Math.random() * 2 - 1) * 0.5;
      }

      this.skidNoise = this.ctx.createBufferSource();
      this.skidNoise.buffer = noiseBuffer;
      this.skidNoise.loop = true;

      const skidFilter = this.ctx.createBiquadFilter();
      skidFilter.type = 'bandpass';
      skidFilter.frequency.setValueAtTime(1200, this.ctx.currentTime);
      skidFilter.Q.setValueAtTime(3, this.ctx.currentTime);

      this.skidGain = this.ctx.createGain();
      this.skidGain.gain.setValueAtTime(0.0001, this.ctx.currentTime);

      this.skidNoise.connect(skidFilter);
      skidFilter.connect(this.skidGain);
      this.skidGain.connect(this.ambientBus);
      this.skidNoise.start();

      // Rain ambient noise generator (soothing low-pass filtered hiss)
      const rainBufSize = this.ctx.sampleRate * 2;
      const rainBuffer = this.ctx.createBuffer(1, rainBufSize, this.ctx.sampleRate);
      const rainOut = rainBuffer.getChannelData(0);
      for (let i = 0; i < rainBufSize; i++) {
        rainOut[i] = (Math.random() * 2 - 1) * 0.35;
      }
      this.rainNoise = this.ctx.createBufferSource();
      this.rainNoise.buffer = rainBuffer;
      this.rainNoise.loop = true;

      this.rainFilter = this.ctx.createBiquadFilter();
      this.rainFilter.type = 'lowpass';
      this.rainFilter.frequency.setValueAtTime(800, this.ctx.currentTime);

      this.rainGain = this.ctx.createGain();
      this.rainGain.gain.setValueAtTime(0.0001, this.ctx.currentTime);

      this.rainNoise.connect(this.rainFilter);
      this.rainFilter.connect(this.rainGain);
      this.rainGain.connect(this.ambientBus);
      this.rainNoise.start();

      // Mist / Wind ambient generator (soft low synth rumble)
      this.windOsc = this.ctx.createOscillator();
      this.windOsc.type = 'sine';
      this.windOsc.frequency.setValueAtTime(72, this.ctx.currentTime);

      this.windGain = this.ctx.createGain();
      this.windGain.gain.setValueAtTime(0.0001, this.ctx.currentTime);

      this.windOsc.connect(this.windGain);
      this.windGain.connect(this.ambientBus);
      this.windOsc.start();

      this.isInitialized = true;
    } catch (e) {
      console.warn('Web Audio not supported or blocked:', e);
    }
  }

  setWeather(weather) {
    if (!this.ctx || !this.isInitialized) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();

    const now = this.ctx.currentTime;
    const targetRain = (this.enabled && weather === 'rain') ? 0.034 : 0.0001;
    const targetWind = (this.enabled && weather === 'mist') ? 0.022 : 0.0001;

    if (this.rainGain) {
      this.rainGain.gain.setTargetAtTime(targetRain, now, 0.4);
    }
    if (this.windGain) {
      this.windGain.gain.setTargetAtTime(targetWind, now, 0.5);
    }
  }

  playThunder() {
    if (!this.enabled || !this.ctx || !this.isInitialized) return;
    try {
      const now = this.ctx.currentTime;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      const filter = this.ctx.createBiquadFilter();

      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(54, now);
      osc.frequency.exponentialRampToValueAtTime(18, now + 1.6);

      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(160, now);
      filter.frequency.exponentialRampToValueAtTime(60, now + 1.6);

      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.linearRampToValueAtTime(0.12, now + 0.15);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.8);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.effectBus);

      osc.start(now);
      osc.stop(now + 1.9);
    } catch (_) {}
  }

  playSplash() {
    if (!this.enabled || !this.ctx || !this.isInitialized) return;
    try {
      const now = this.ctx.currentTime;
      const bufferSize = Math.floor(this.ctx.sampleRate * 0.16);
      const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.3));
      }
      const noise = this.ctx.createBufferSource();
      noise.buffer = buffer;

      const filter = this.ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(1300, now);
      filter.frequency.exponentialRampToValueAtTime(420, now + 0.16);
      filter.Q.setValueAtTime(1.8, now);

      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0.06, now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);

      noise.connect(filter);
      filter.connect(gain);
      gain.connect(this.effectBus);

      noise.start(now);
    } catch (_) {}
  }

  updateEngine(speedFraction, isThrottling) {
    if (!this.enabled || !this.ctx || !this.isInitialized) return;
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }

    const targetFreq = 50 + speedFraction * 140 + (isThrottling ? 25 : 0);
    const targetGain = 0.03 + (isThrottling ? 0.05 : 0.01) * (0.4 + speedFraction * 0.6);

    this.engineOsc.frequency.setTargetAtTime(targetFreq, this.ctx.currentTime, 0.08);
    this.engineGain.gain.setTargetAtTime(targetGain, this.ctx.currentTime, 0.08);
  }

  setDriftSound(isDrifting) {
    if (!this.enabled || !this.ctx || !this.skidGain) return;
    const targetGain = isDrifting ? 0.08 : 0.0001;
    this.skidGain.gain.setTargetAtTime(targetGain, this.ctx.currentTime, 0.05);
  }

  startHorn(isVan = false) {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();
    if (this.isHonking) return;

    this.isHonking = true;
    try {
      const now = this.ctx.currentTime;
      this.hornOsc1 = this.ctx.createOscillator();
      this.hornOsc2 = this.ctx.createOscillator();
      this.hornOscSub = this.ctx.createOscillator();
      this.hornGain = this.ctx.createGain();
      const filter = this.ctx.createBiquadFilter();

      this.hornOsc1.type = 'sawtooth';
      this.hornOsc2.type = 'sawtooth';
      this.hornOscSub.type = 'triangle';

      if (isVan) {
        // Deep, commanding bakery delivery van air-horn (F3 174.6Hz & Bb3 233.1Hz + deep sub 87.3Hz)
        this.hornOsc1.frequency.setValueAtTime(174.61, now);
        this.hornOsc2.frequency.setValueAtTime(233.08, now);
        this.hornOscSub.frequency.setValueAtTime(87.31, now);
      } else {
        // Warm vintage dual-tone Italian scooter horn (F4 349.2Hz & A4 440Hz + sub 174.6Hz)
        this.hornOsc1.frequency.setValueAtTime(349.23, now);
        this.hornOsc2.frequency.setValueAtTime(440.00, now);
        this.hornOscSub.frequency.setValueAtTime(174.61, now);
      }

      // Acoustic vibration flutter (LFO)
      const lfo = this.ctx.createOscillator();
      const lfoGain = this.ctx.createGain();
      lfo.frequency.setValueAtTime(isVan ? 16 : 22, now);
      lfoGain.gain.setValueAtTime(isVan ? 6.5 : 4.5, now);
      lfo.connect(lfoGain);
      lfoGain.connect(this.hornOsc1.frequency);
      lfoGain.connect(this.hornOsc2.frequency);
      lfo.start(now);
      this.hornLfo = lfo;

      // Lowpass filter for warm retro timbre without digital harshness
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(isVan ? 1350 : 1900, now);
      filter.Q.setValueAtTime(1.8, now);

      // Clean attack & steady sustain for as long as held
      this.hornGain.gain.setValueAtTime(0.0001, now);
      this.hornGain.gain.linearRampToValueAtTime(isVan ? 0.24 : 0.18, now + 0.025);

      this.hornOsc1.connect(filter);
      this.hornOsc2.connect(filter);
      this.hornOscSub.connect(filter);
      filter.connect(this.hornGain);
      this.hornGain.connect(this.alertBus);

      this.hornOsc1.start(now);
      this.hornOsc2.start(now);
      this.hornOscSub.start(now);
    } catch (e) {
      console.warn('Start horn error:', e);
      this.isHonking = false;
    }
  }

  stopHorn() {
    if (!this.isHonking || !this.ctx || !this.hornGain) {
      this.isHonking = false;
      return;
    }
    this.isHonking = false;
    try {
      const now = this.ctx.currentTime;
      const stopTime = now + 0.08;
      this.hornGain.gain.cancelScheduledValues(now);
      this.hornGain.gain.setValueAtTime(this.hornGain.gain.value, now);
      this.hornGain.gain.exponentialRampToValueAtTime(0.0001, stopTime);

      const o1 = this.hornOsc1;
      const o2 = this.hornOsc2;
      const os = this.hornOscSub;
      const lfo = this.hornLfo;
      const g = this.hornGain;

      setTimeout(() => {
        try {
          if (o1) { o1.stop(); o1.disconnect(); }
          if (o2) { o2.stop(); o2.disconnect(); }
          if (os) { os.stop(); os.disconnect(); }
          if (lfo) { lfo.stop(); lfo.disconnect(); }
          if (g) { g.disconnect(); }
        } catch (_) {}
      }, 90);

      this.hornOsc1 = null;
      this.hornOsc2 = null;
      this.hornOscSub = null;
      this.hornLfo = null;
      this.hornGain = null;
    } catch (e) {
      this.isHonking = false;
    }
  }

  playHorn(duration = 0.28) {
    this.startHorn();
    setTimeout(() => {
      this.stopHorn();
    }, duration * 1000);
  }

  playBump() {
    if (!this.enabled || !this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(140, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(30, this.ctx.currentTime + 0.15);

    gain.gain.setValueAtTime(0.15, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.15);

    osc.connect(gain);
    gain.connect(this.effectBus);

    osc.start();
    osc.stop(this.ctx.currentTime + 0.15);
  }

  playCrash(intensity = 0.5) {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(110, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(25, this.ctx.currentTime + 0.22);

    const vol = Math.min(0.28, 0.08 + intensity * 0.18);
    gain.gain.setValueAtTime(vol, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.22);

    osc.connect(gain);
    gain.connect(this.effectBus);

    osc.start();
    osc.stop(this.ctx.currentTime + 0.22);
  }

  playChime() {
    this.duck();
    if (!this.enabled || !this.ctx) return;
    const notes = [523.25, 659.25, 783.99, 1046.50]; // C - E - G - High C
    notes.forEach((freq, idx) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, this.ctx.currentTime + idx * 0.08);

      gain.gain.setValueAtTime(0.0001, this.ctx.currentTime);
      gain.gain.setValueAtTime(0.1, this.ctx.currentTime + idx * 0.08);
      gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + idx * 0.08 + 0.35);

      osc.connect(gain);
      gain.connect(this.effectBus);

      osc.start(this.ctx.currentTime + idx * 0.08);
      osc.stop(this.ctx.currentTime + idx * 0.08 + 0.35);
    });
  }

  // Fase 2: Fragility shock / pastry damage rattle
  playPastryDamage() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();

    // Metallic box clatter & crunch
    const freqs = [220, 180, 140];
    freqs.forEach((freq, i) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, this.ctx.currentTime + i * 0.04);
      osc.frequency.exponentialRampToValueAtTime(60, this.ctx.currentTime + i * 0.04 + 0.12);

      gain.gain.setValueAtTime(0.14, this.ctx.currentTime + i * 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + i * 0.04 + 0.12);

      osc.connect(gain);
      gain.connect(this.effectBus);
      osc.start(this.ctx.currentTime + i * 0.04);
      osc.stop(this.ctx.currentTime + i * 0.04 + 0.12);
    });
  }

  // Fase 2: Delivery tip & coin rattle sound
  playCoinSound() {
    this.duck();
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();

    const coins = [987.77, 1318.51, 1567.98, 2093.00];
    coins.forEach((freq, idx) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, this.ctx.currentTime + idx * 0.06);

      gain.gain.setValueAtTime(0.12, this.ctx.currentTime + idx * 0.06);
      gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + idx * 0.06 + 0.2);

      osc.connect(gain);
      gain.connect(this.effectBus);
      osc.start(this.ctx.currentTime + idx * 0.06);
      osc.stop(this.ctx.currentTime + idx * 0.06 + 0.2);
    });
  }

  playCoin() {
    this.playCoinSound();
  }

  // Fase 4: Garage Upgrade & Tuning purchase chime
  playUpgradeSound() {
    this.duck();
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();

    const notes = [523.25, 659.25, 783.99, 1046.50, 1318.51];
    notes.forEach((freq, idx) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = (idx === 0) ? 'triangle' : 'sine';
      osc.frequency.setValueAtTime(freq, this.ctx.currentTime + idx * 0.05);

      gain.gain.setValueAtTime(0.0001, this.ctx.currentTime);
      gain.gain.setValueAtTime(0.12, this.ctx.currentTime + idx * 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + idx * 0.05 + 0.28);

      osc.connect(gain);
      gain.connect(this.effectBus);
      osc.start(this.ctx.currentTime + idx * 0.05);
      osc.stop(this.ctx.currentTime + idx * 0.05 + 0.28);
    });
  }

  // Bouncy plastic traffic cone kick / impact
  playConeKick() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();

    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(260, now);
    osc.frequency.exponentialRampToValueAtTime(70, now + 0.14);

    gain.gain.setValueAtTime(0.18, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.14);

    osc.connect(gain);
    gain.connect(this.effectBus);
    osc.start(now);
    osc.stop(now + 0.14);
  }

  // Short impatient traffic car horn in queue
  playTrafficCarHorn() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();

    const now = this.ctx.currentTime;
    const osc1 = this.ctx.createOscillator();
    const osc2 = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc1.type = 'sawtooth';
    osc2.type = 'sawtooth';
    osc1.frequency.setValueAtTime(415, now);
    osc2.frequency.setValueAtTime(518, now);

    gain.gain.setValueAtTime(0.001, now);
    gain.gain.linearRampToValueAtTime(0.08, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(this.alertBus);

    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 0.22);
    osc2.stop(now + 0.22);
  }

  // Friendly double-beep car response ("Tuut tuut!")
  playFriendlyDoubleHorn() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();

    const now = this.ctx.currentTime;
    [0, 0.13].forEach((delay) => {
      const osc1 = this.ctx.createOscillator();
      const osc2 = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc1.type = 'sawtooth';
      osc2.type = 'sine';
      osc1.frequency.setValueAtTime(440, now + delay);
      osc2.frequency.setValueAtTime(554.37, now + delay); // C#5 warm harmonic

      gain.gain.setValueAtTime(0.001, now + delay);
      gain.gain.linearRampToValueAtTime(0.09, now + delay + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.09);

      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(this.alertBus);

      osc1.start(now + delay);
      osc2.start(now + delay);
      osc1.stop(now + delay + 0.09);
      osc2.stop(now + delay + 0.09);
    });
  }

  // Fase 2: Neighborhood cat meow synth
  playCatMeow() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sine';

    // Meow frequency sweep: rising then falling gently
    const now = this.ctx.currentTime;
    osc.frequency.setValueAtTime(560, now);
    osc.frequency.exponentialRampToValueAtTime(820, now + 0.12);
    osc.frequency.exponentialRampToValueAtTime(450, now + 0.32);

    gain.gain.setValueAtTime(0.001, now);
    gain.gain.linearRampToValueAtTime(0.12, now + 0.06);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.32);

    osc.connect(gain);
    gain.connect(this.effectBus);
    osc.start(now);
    osc.stop(now + 0.32);
  }

  // Comical / dramatic startled cat yowl/screech when struck
  playCatScreech() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    try {
      const now = this.ctx.currentTime;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      const filter = this.ctx.createBiquadFilter();

      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(1150, now);
      osc.frequency.exponentialRampToValueAtTime(750, now + 0.09);
      osc.frequency.exponentialRampToValueAtTime(280, now + 0.42);

      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(2200, now);

      gain.gain.setValueAtTime(0.001, now);
      gain.gain.linearRampToValueAtTime(0.24, now + 0.03);
      gain.gain.setValueAtTime(0.24, now + 0.16);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.44);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.effectBus);
      osc.start(now);
      osc.stop(now + 0.44);
    } catch (_) {}
  }

  // Authentic Dutch Two-Tone Police Siren (Martinshorn: A4 440Hz & D5 587Hz)
  startPoliceSiren() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    if (this.policeSirenInterval) return;

    const playTone = (isHi) => {
      if (!this.enabled || !this.ctx) return;
      try {
        const now = this.ctx.currentTime;
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        const filter = this.ctx.createBiquadFilter();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(isHi ? 587.33 : 440.00, now);

        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(1500, now);

        gain.gain.setValueAtTime(0.001, now);
        gain.gain.linearRampToValueAtTime(0.13, now + 0.02);
        gain.gain.setValueAtTime(0.13, now + 0.31);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(this.alertBus);

        osc.start(now);
        osc.stop(now + 0.35);
      } catch (_) {}
    };

    let isHigh = true;
    playTone(isHigh);
    this.policeSirenInterval = setInterval(() => {
      isHigh = !isHigh;
      playTone(isHigh);
    }, 360);
  }

  stopPoliceSiren() {
    if (this.policeSirenInterval) {
      clearInterval(this.policeSirenInterval);
      this.policeSirenInterval = null;
    }
  }

  // Police Traffic Whistle (Two-tone shrill trill)
  playPoliceWhistle() {
    this.duck();
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    try {
      const now = this.ctx.currentTime;
      [0, 0.12].forEach((delay) => {
        const osc1 = this.ctx.createOscillator();
        const osc2 = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc1.type = 'sine';
        osc2.type = 'triangle';
        osc1.frequency.setValueAtTime(2450, now + delay);
        osc2.frequency.setValueAtTime(2540, now + delay);

        gain.gain.setValueAtTime(0.001, now + delay);
        gain.gain.linearRampToValueAtTime(0.16, now + delay + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.11);

        osc1.connect(gain);
        osc2.connect(gain);
        gain.connect(this.alertBus);

        osc1.start(now + delay);
        osc2.start(now + delay);
        osc1.stop(now + delay + 0.11);
        osc2.stop(now + delay + 0.11);
      });
    } catch (_) {}
  }

  // Camera Flash & High-speed electronic shutter sound (Politie Flitser)
  playCameraFlash() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    try {
      const now = this.ctx.currentTime;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(3400, now);
      osc.frequency.exponentialRampToValueAtTime(1200, now + 0.18);
      gain.gain.setValueAtTime(0.24, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
      osc.connect(gain);
      gain.connect(this.effectBus);
      osc.start(now);
      osc.stop(now + 0.22);

      const bufferSize = Math.floor(this.ctx.sampleRate * 0.08);
      const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.25));
      }
      const noise = this.ctx.createBufferSource();
      noise.buffer = buffer;
      const noiseGain = this.ctx.createGain();
      noiseGain.gain.setValueAtTime(0.35, now);
      noiseGain.gain.exponentialRampToValueAtTime(0.01, now + 0.08);
      noise.connect(noiseGain);
      noiseGain.connect(this.effectBus);
      noise.start(now);
    } catch (_) {}
  }

  // Warning double-beep for cold food or damaged box
  playWarning() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();

    [0, 0.12].forEach((offset) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, this.ctx.currentTime + offset);

      gain.gain.setValueAtTime(0.08, this.ctx.currentTime + offset);
      gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + offset + 0.08);

      osc.connect(gain);
      gain.connect(this.alertBus);
      osc.start(this.ctx.currentTime + offset);
      osc.stop(this.ctx.currentTime + offset + 0.08);
    });
  }

  // Realistic retro car horn (dual tone 349Hz & 440Hz with filtered warmth)
  playCarHorn() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();
    const now = this.ctx.currentTime;

    const osc1 = this.ctx.createOscillator();
    const osc2 = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();

    osc1.type = 'sawtooth';
    osc2.type = 'sawtooth';
    osc1.frequency.setValueAtTime(349.23, now); // F4
    osc2.frequency.setValueAtTime(440.00, now); // A4

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1250, now);

    gain.gain.setValueAtTime(0.001, now);
    gain.gain.linearRampToValueAtTime(0.12, now + 0.02);
    gain.gain.setValueAtTime(0.12, now + 0.16);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);

    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gain);
    gain.connect(this.alertBus);

    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 0.28);
    osc2.stop(now + 0.28);
  }

  // Flitsmeister High-Tech Radar Beep Alert ("Biep-biep! Nadering flitspaal")
  playFlitsmeisterBeep() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    try {
      const now = this.ctx.currentTime;
      [0, 0.11].forEach((delay) => {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(1850, now + delay);
        osc.frequency.exponentialRampToValueAtTime(2400, now + delay + 0.06);
        gain.gain.setValueAtTime(0.001, now + delay);
        gain.gain.linearRampToValueAtTime(0.18, now + delay + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.08);

        osc.connect(gain);
        gain.connect(this.alertBus);
        osc.start(now + delay);
        osc.stop(now + delay + 0.08);
      });
    } catch (_) {}
  }

  // Authentic Dutch Bicycle Bell ("Tring tring!")
  playBicycleBell() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    try {
      const now = this.ctx.currentTime;
      [0, 0.09].forEach((delay) => {
        const osc1 = this.ctx.createOscillator();
        const osc2 = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc1.type = 'sine';
        osc2.type = 'triangle';
        osc1.frequency.setValueAtTime(2850, now + delay);
        osc2.frequency.setValueAtTime(3420, now + delay);

        gain.gain.setValueAtTime(0.001, now + delay);
        gain.gain.linearRampToValueAtTime(0.14, now + delay + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.12);

        osc1.connect(gain);
        osc2.connect(gain);
        gain.connect(this.alertBus);

        osc1.start(now + delay);
        osc2.start(now + delay);
        osc1.stop(now + delay + 0.12);
        osc2.stop(now + delay + 0.12);
      });
    } catch (_) {}
  }

  // Dutch Canal Drawbridge Warning Bell Gong ("Ding... ding... ding...")
  playBridgeBell() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    try {
      const now = this.ctx.currentTime;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1450, now);
      osc.frequency.exponentialRampToValueAtTime(800, now + 0.25);

      gain.gain.setValueAtTime(0.001, now);
      gain.gain.linearRampToValueAtTime(0.15, now + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);

      osc.connect(gain);
      gain.connect(this.alertBus);
      osc.start(now);
      osc.stop(now + 0.28);
    } catch (_) {}
  }

  // Deep resonant Dutch Inland Ship / Barge Horn ("TOOOOOOOOT!")
  playShipHorn() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    try {
      const now = this.ctx.currentTime;
      const osc1 = this.ctx.createOscillator();
      const osc2 = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      const filter = this.ctx.createBiquadFilter();

      osc1.type = 'sawtooth';
      osc2.type = 'triangle';
      osc1.frequency.setValueAtTime(130.81, now); // C3
      osc2.frequency.setValueAtTime(164.81, now); // E3

      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(680, now);

      gain.gain.setValueAtTime(0.001, now);
      gain.gain.linearRampToValueAtTime(0.18, now + 0.08);
      gain.gain.setValueAtTime(0.18, now + 0.65);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.85);

      osc1.connect(filter);
      osc2.connect(filter);
      filter.connect(gain);
      gain.connect(this.alertBus);

      osc1.start(now);
      osc2.start(now);
      osc1.stop(now + 0.85);
      osc2.stop(now + 0.85);
    } catch (_) {}
  }

  toggleMute() {
    this.enabled = !this.enabled;
    if (!this.enabled) {
      this.stopHorn();
      this.stopPoliceSiren();
      if (this.engineGain && this.ctx) {
        this.engineGain.gain.setValueAtTime(0.0001, this.ctx.currentTime);
      }
      if (this.skidGain && this.ctx) {
        this.skidGain.gain.setValueAtTime(0.0001, this.ctx.currentTime);
      }
    }
    return this.enabled;
  }

  // Arcade Game Audio: Snappy 16-bit UI Click
  playUiClick() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    try {
      const now = this.ctx.currentTime;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(580, now);
      osc.frequency.exponentialRampToValueAtTime(880, now + 0.04);
      gain.gain.setValueAtTime(0.08, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
      osc.connect(gain);
      gain.connect(this.effectBus);
      osc.start(now);
      osc.stop(now + 0.05);
    } catch (_) {}
  }

  // Arcade Game Audio: Tactile Paper / Ink Stamp Thud
  playStamp() {
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    try {
      const now = this.ctx.currentTime;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(160, now);
      osc.frequency.exponentialRampToValueAtTime(45, now + 0.12);
      gain.gain.setValueAtTime(0.22, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.13);
      osc.connect(gain);
      gain.connect(this.effectBus);
      osc.start(now);
      osc.stop(now + 0.13);
    } catch (_) {}
  }

  // Arcade Game Audio: Authentic Bakery Cash Register / Kassa Ding (Ka-ching!)
  playCashRegister() {
    this.duck();
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    try {
      const now = this.ctx.currentTime;
      // Mechanical latch click
      const click = this.ctx.createOscillator();
      const clickGain = this.ctx.createGain();
      click.type = 'triangle';
      click.frequency.setValueAtTime(320, now);
      click.frequency.exponentialRampToValueAtTime(120, now + 0.03);
      clickGain.gain.setValueAtTime(0.15, now);
      clickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.035);
      click.connect(clickGain);
      clickGain.connect(this.effectBus);
      click.start(now);
      click.stop(now + 0.035);

      // Bell chime (high pitched dual ping)
      const freqs = [1760, 2637];
      freqs.forEach((f, i) => {
        const bell = this.ctx.createOscillator();
        const bellGain = this.ctx.createGain();
        bell.type = 'sine';
        bell.frequency.setValueAtTime(f, now + 0.04 + i * 0.06);
        bellGain.gain.setValueAtTime(0.001, now);
        bellGain.gain.setValueAtTime(0.16, now + 0.04 + i * 0.06);
        bellGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.04 + i * 0.06 + 0.4);
        bell.connect(bellGain);
        bellGain.connect(this.effectBus);
        bell.start(now + 0.04 + i * 0.06);
        bell.stop(now + 0.04 + i * 0.06 + 0.4);
      });
    } catch (_) {}
  }

  // Arcade Game Audio: Triumphant Combo Fanfare (C5, E5, G5, C6)
  playComboFanfare() {
    this.duck();
    if (!this.enabled || !this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    try {
      const notes = [523.25, 659.25, 783.99, 1046.50];
      const now = this.ctx.currentTime;
      notes.forEach((freq, idx) => {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, now + idx * 0.07);
        gain.gain.setValueAtTime(0.001, now);
        gain.gain.setValueAtTime(0.14, now + idx * 0.07);
        gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.07 + 0.3);
        osc.connect(gain);
        gain.connect(this.effectBus);
        osc.start(now + idx * 0.07);
        osc.stop(now + idx * 0.07 + 0.3);
      });
    } catch (_) {}
  }

  /** Zet de motor en het weer even zachter, zodat een melding goed hoorbaar is. */
  duck(amount = 0.35, seconds = 0.5) {
    if (!this.ctx || !this.ambientBus) return;
    const now = this.ctx.currentTime;
    const g = this.ambientBus.gain;
    g.cancelScheduledValues(now);
    g.setTargetAtTime(0.85 * amount, now, 0.03);
    g.setTargetAtTime(0.85, now + seconds, 0.25);
  }

  /** Zet één kanaal ('motor', 'alarm' of 'effects') op een niveau van 0 tot 1. Werkt ook voordat het geluid is gestart. */
  setChannelLevel(channel, level) {
    if (!(channel in this.levels)) return;
    this.levels[channel] = level;
    const node = { motor: this.ambientLevel, alarm: this.alertBus, effects: this.effectBus }[channel];
    if (node && this.ctx) node.gain.setTargetAtTime(level, this.ctx.currentTime, 0.05);
    // Een toeter die je op dat moment ingedrukt houdt, stopt als dit kanaal uit gaat
    if (channel === 'alarm' && level === 0) this.stopHorn();
  }

  /** Geluid aan of uit via het hoofdvolume. */
  setEnabled(on) {
    this.enabled = on;
    if (!on) {
      this.stopHorn();
      this.stopPoliceSiren();
    }
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(on ? 1 : 0, this.ctx.currentTime, 0.05);
  }
}
